import { useEffect, useState } from "react";

import styles from "@styles/beneficios/steps/BenefitTypeSelectStep.module.scss";

import { Checkbox, Label, Loading, Select } from "@components";
import { Button } from "@components/Button";
import { Hairline } from "@components/Hairline";
import { Message } from "@components/Message";
import { Radio } from "@components/Radio";
import { TimeInput } from "@components/TimeInput";
import { Icon } from "@components/core/Icon";
import classNames from "classnames/bind";
import ROLES from "data/Roles";
import { UserStorage } from "shared/tools/UserStorage";
import { AssetsAPI } from "providers/AssetsAPI";
import * as uuid from "uuid";
import { Picture } from "shared/components/Icons";
import { If } from "shared/components/control-statements";
const css = classNames.bind(styles);

const days = [
  {
    key: 0,
    short: "D",
    full: "domingo",
  },
  {
    key: 1,
    short: "S",
    full: "segunda-feira",
  },
  {
    key: 2,
    short: "T",
    full: "terça-feira",
  },
  {
    key: 3,
    short: "Q",
    full: "quarta-feira",
  },
  {
    key: 4,
    short: "Q",
    full: "quinta-feira",
  },
  {
    key: 5,
    short: "S",
    full: "sexta-feira",
  },
  {
    key: 6,
    short: "S",
    full: "sábado",
  },
];

export function ProductAvailabilityStep({ catalog, item, onChange }) {
  const [visibility, setVisibility] = useState(item?.availability || []);
  const [availableAtAllTimes, setAvailableAtAllTimes] = useState(
    !item?.availability?.length
  );
  const [loading, setLoading] = useState(false);

  const categories = catalog?.items.map((c) => {
    return {
      label: c.name,
      value: c.ref_id,
    };
  });

  useEffect(() => {
    const removeColons = (array) => {
      return array.map((item) => ({
        ...item,
        start: item.start,
        end: item.end,
      }));
    };

    const updatedVisibility = visibility.map((weekDay) => ({
      ...weekDay,
      times: removeColons(weekDay.times),
    }));

    onChange({
      ...item,
      availability: !!availableAtAllTimes ? null : updatedVisibility,
    });
  }, [visibility, availableAtAllTimes]);

  const isSelectedDay = (day) => {
    return visibility?.some((d) => d.weekDay == day);
  };

  const selectDaysWeek = (day) => {
    setVisibility((prevVisibility) => {
      const updatedVisibility = [...prevVisibility];

      const targetObject = updatedVisibility.find((obj) => obj.weekDay === day);

      if (targetObject) {
        updatedVisibility.splice(updatedVisibility.indexOf(targetObject), 1);
      } else {
        const weekDay = {
          type: "week_day",
          weekDay: day,
          times: [
            {
              start: "00:00",
              end: "12:00",
            },
          ],
        };
        updatedVisibility.push(weekDay);
        updatedVisibility.sort((a, b) => a.weekDay - b.weekDay);
      }

      return updatedVisibility;
    });
  };

  const addOrRemoveTime = (weekDay, newTime, indexToRemove) => {
    setVisibility((prevVisibility) => {
      const updatedVisibility = [...prevVisibility];

      const targetObject = updatedVisibility.find(
        (obj) => obj.weekDay === weekDay
      );

      if (indexToRemove !== undefined && indexToRemove >= 0) {
        // Remover o tempo específico
        targetObject.times.splice(indexToRemove, 1);
      } else {
        // Adicionar novo tempo ao objeto existente ou criar um novo objeto
        if (targetObject) {
          targetObject.times.push(newTime);
        } else {
          const newObject = {
            type: "week_day",
            weekDay,
            times: [newTime],
          };
          updatedVisibility.push(newObject);
        }
      }

      return updatedVisibility;
    });
  };

  const changeTime = (weekDay, timeIndex, field, value) => {
    setVisibility((prevVisibility) => {
      const updatedVisibility = [...prevVisibility];

      const targetObject = updatedVisibility.find(
        (obj) => obj.weekDay === weekDay
      );

      if (targetObject) {
        targetObject.times[timeIndex][field] = value;
      }

      return updatedVisibility;
    });
  };

  const checkTimeErrors = (weekDay) => {
    const conflicts = [];

    const targetObject = visibility.find((obj) => obj.weekDay === weekDay);

    if (targetObject && targetObject.times) {
      targetObject.times.forEach((time) => {
        if (time.start === time.end || time.start >= time.end) {
          conflicts.push({
            weekDay: weekDay,
            conflictingTime: time,
          });
        }
      });
    }

    return conflicts.length > 0;
  };

  let userRole = UserStorage.getLoggedUser()?.role;
  let username = UserStorage.getLoggedUser()?.username;

  const handleSetImage = async (e) => {
    setLoading(true);
    const file = e.target.files?.[0];
    if (!file) return false;

    const maxFileSizeMB = 5;
    const fileSizeMB = file.size / 1024 / 1024;

    if (fileSizeMB > maxFileSizeMB) {
      alert({ text: `Escolha um arquivo com no máximo ${maxFileSizeMB}MB` });
      return false;
    }

    if (!/^image\//.test(file.type)) {
      alert({ text: "Arquivo inválido. Selecione uma imagem." });
      return false;
    }

    const reader = new FileReader();
    const imageSrc = await new Promise((resolve, reject) => {
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = () => reject(new Error("Erro ao ler o arquivo."));
      reader.readAsDataURL(file);
    });

    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = () => {
        if (img.width > 500 || img.height > 500) {
          setLoading(false);
          alert({ text: "A imagem deve ter dimensões de até 500x500." });
        } else resolve();
      };
      img.onerror = () => {
        setLoading(false);
        alert({ text: "Erro ao carregar a imagem." });
      };
      img.src = imageSrc;
    });

    try {
      const response = await AssetsAPI.PostAssets({
        mime_type: file.type,
        file: imageSrc.split("base64,")[1],
      });
      const image = response.getData(null);
      setLoading(false);
      return image;
    } catch (err) {
      console.error("Erro ao enviar imagem:", err);
      alert({ text: "Erro ao enviar imagem. Tente novamente." });
      setLoading(false);
      return false;
    }
  };

  const handleImage = async (e) => {
    const image = await handleSetImage(e);
    if (image) {
      onChange({
        ...item,
        images: [
          {
            id: uuid.v4(),
            name: "Tabela nutricional",
            category: "NUTRITION_FACTS",
            url: image,
          },
        ],
      });
    }
  };

  return (
    <>
      <Label>Informe a disponibilidade do produto</Label>
      <Radio
        family="availability"
        className="mb-10"
        value={true}
        checked={availableAtAllTimes}
        onChange={() => setAvailableAtAllTimes(true)}
      >
        A todo momento
      </Radio>
      <Radio
        family="availability"
        value={false}
        checked={availableAtAllTimes}
        onChange={() => setAvailableAtAllTimes(false)}
      >
        Disponível em horários específicos
      </Radio>

      {availableAtAllTimes === false && (
        <>
          <Hairline />
          <div className={styles.days}>
            {days.map((day) => (
              <div
                className={css({
                  day: true,
                  isSelected: isSelectedDay(day.key),
                })}
                onClick={() => selectDaysWeek(day.key)}
                key={day.full}
              >
                {day.short}
              </div>
            ))}
          </div>

          {visibility.map((v) => {
            const isIncluded = days.some((day) => day.key === v.weekDay);

            if (!isIncluded) return null;

            return (
              <div className={styles.times} key={v.weekDay}>
                <div className={styles.label}>
                  Horário <strong>{days[v.weekDay].full}:</strong>
                </div>
                {v.times.map((time, timeIndex) => {
                  const hourStart = time.start.substring(0, 2);
                  const minutesStart = time.start.substring(
                    time.start.length - 2
                  );
                  const hourEnd = time.end.substring(0, 2);
                  const minutesEnd = time.end.substring(time.end.length - 2);

                  const startTime = hourStart + ":" + minutesStart;
                  const endTime = hourEnd + ":" + minutesEnd;

                  return (
                    <div className="d-flex align-stretch mt-30" key={timeIndex}>
                      <TimeInput
                        value={startTime}
                        onChange={(value) =>
                          changeTime(v.weekDay, timeIndex, "start", value)
                        }
                        label="De"
                        className="pt-0 mb-0 mr-20"
                      />
                      <TimeInput
                        value={endTime}
                        onChange={(value) =>
                          changeTime(v.weekDay, timeIndex, "end", value)
                        }
                        label="Até"
                        className="pt-0 mb-0 mr-20"
                      />
                      <Button
                        className="mr-15"
                        onClick={() =>
                          addOrRemoveTime(v.weekDay, {
                            start: "00:00",
                            end: "12:00",
                          })
                        }
                      >
                        <Icon name="plus" />
                      </Button>
                      <Button
                        onClick={() =>
                          addOrRemoveTime(v.weekDay, null, timeIndex)
                        }
                        disabled={v.times.length === 1}
                      >
                        <Icon name="trash-alt" />
                      </Button>
                    </div>
                  );
                })}

                {checkTimeErrors(v.weekDay) && (
                  <Message type="danger" className="mt-20">
                    Verifique os horários
                  </Message>
                )}
              </div>
            );
          })}
        </>
      )}

      <Hairline />

      <Label>Opções da loja online</Label>
      <Checkbox
        design="two"
        className="mb-15"
        checked={item.highlight}
        onChange={(isChecked) => onChange({ highlight: isChecked })}
      >
        Destacar este produto na loja
      </Checkbox>
      <Checkbox
        design="two"
        className="mb-15"
        checked={item.showAll === false}
        onChange={(isChecked) => onChange({ showAll: !isChecked })}
      >
        Ocultar em tudo
      </Checkbox>
      <Checkbox
        design="two"
        className="mb-15"
        checked={item.showTotem === false || item.showAll === false}
        disabled={item.showAll === false}
        onChange={(isChecked) => onChange({ showTotem: !isChecked })}
      >
        Ocultar no Totem
      </Checkbox>
      <Checkbox
        design="two"
        className="mb-15"
        checked={item.showTablet === false || item.showAll === false}
        disabled={item.showAll === false}
        onChange={(isChecked) => onChange({ showTablet: !isChecked })}
      >
        Ocultar no Tablet
      </Checkbox>

      <Checkbox
        design="two"
        className="mb-15"
        checked={item.showQrCode === false || item.showAll === false}
        disabled={item.showAll === false}
        onChange={(isChecked) => onChange({ showQrCode: !isChecked })}
      >
        Ocultar no QRCode
      </Checkbox>

      {(userRole === ROLES.SUPPORT || userRole === ROLES.ADMIN) && (
        <>
          <Checkbox
            design="two"
            className="mb-15"
            checked={item.target_category?.active}
            onChange={(isChecked) => {
              if (!!isChecked)
                onChange({
                  target_category: {
                    ...item.target_category,
                    active: isChecked,
                    key: item.target_category?.key || categories[0]?.value,
                  },
                });
              else
                onChange({
                  target_category: null,
                });
            }}
          >
            Selecionar categoria pós adição (Apenas Totem)
          </Checkbox>
          {!!item.target_category?.active && (
            <Select
              options={categories}
              value={item?.target_category?.key}
              onChange={(e) => {
                onChange({
                  target_category: {
                    ...item.target_category,
                    key: categories.find((c) => c.value === e.target.value)
                      ?.value,
                  },
                });
              }}
            />
          )}
        </>
      )}

      <Checkbox
        design="two"
        className="mb-15"
        checked={(item.images || []).length === 1}
        onChange={(isChecked) => {
          if (!!isChecked)
            onChange({
              images: [{}],
            });
          else
            onChange({
              images: null,
            });
        }}
      >
        Inserir tabela nutricional
      </Checkbox>

      {(item.images || []).length === 1 && (
        <>
          <label
            htmlFor="product-picture"
            className={css({
              uploadImage: true,
              hasImage: item.images?.[0]?.url,
              uploading: loading,
            })}
          >
            <If cond={loading}>
              <Loading />
            </If>
            <If cond={!loading}>
              {item.images?.[0]?.url ? (
                <img
                  src={`${process.env.REACT_APP_ASSETS_URL}/${item.images[0]?.url}`}
                  alt=""
                />
              ) : (
                <>
                  <div className={styles.icon}>
                    <i className="fal fa-wheat" />
                  </div>
                  <div className={styles.selectFile}>
                    <p>Adicionar tabela</p>
                    <span>Aparece no detalhe do produto</span>
                  </div>
                  <span className={styles.fileAccepted}>
                    JPG, JPEG, PNG, SVG (500x500)
                  </span>
                  <span className={styles.maxSize}>
                    Peso máximo do arquivo: 1,5MB
                  </span>
                </>
              )}
            </If>

            <input
              key={"file-input-" + uuid.v4()}
              type="file"
              id="product-picture"
              accept=".png, .jpeg, .jpg, .svg"
              onChange={handleImage}
            />

            <div className={styles.edit} style={{ pointerEvents: "none" }}>
              <Button onClick={() => {}}>Alterar imagem</Button>
            </div>
          </label>
        </>
      )}
    </>
  );
}
